import { SeloDaCampanha } from '../cptu/CampanhaMoldura';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  criarCampanhaSpt, listarCampanhasSpt, metro, type CampanhaSptDaLista,
} from '../../api/spt';
import { SeletorDeOs } from '../../Filtros';
import { dataDoDia, plural } from '../../formato';
import { useSessao } from '../../sessao';
import { Campo, Carregando, Dialogo, ErroDaApi, Vazio } from '../../ui';

/**
 * A lista das campanhas SPT — onda 356 (spec 12 §5.1; RN-276). A campanha
 * nasce de uma OS (aqui ou no Campo da OS) e abre na ficha do primeiro furo.
 */
export function CampanhasSpt() {
  const { pode } = useSessao();
  const [busca, setBusca] = useState('');
  const [linhas, setLinhas] = useState<CampanhaSptDaLista[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  const [nova, setNova] = useState(false);

  useEffect(() => {
    let vivo = true;
    const t = setTimeout(() => {
      listarCampanhasSpt({ busca: busca.trim() || undefined })
        .then((r) => { if (vivo) setLinhas(r); }).catch((e) => { if (vivo) setErro(e); });
    }, 250);
    return () => { vivo = false; clearTimeout(t); };
  }, [busca]);

  return (
    <div>
      <div className="cabeca">
        <div className="linha-topo">
          <h1 className="cresce">Sondagens SPT</h1>
          {pode('spt.editar') && (
            <button type="button" className="botao" onClick={() => setNova(true)}>＋ Nova campanha</button>
          )}
        </div>
        <p className="sub">Transcrição dos boletins de campo (NBR 6484) até o relatório — mais recentes primeiro.</p>
      </div>
      <div className="cartao">
        <input className="campo" type="search" aria-label="Buscar campanha, OS, cliente ou obra"
          placeholder="Buscar campanha, OS, cliente ou obra" value={busca} onChange={(e) => setBusca(e.target.value)} />
        {erro ? <ErroDaApi erro={erro} />
          : !linhas ? <Carregando texto="Carregando as campanhas SPT" />
          : linhas.length === 0 ? <Vazio>Nenhuma campanha SPT{busca ? ' com essa busca' : ''}.</Vazio>
          : (
            <div className="rola">
              <table>
                <thead>
                  <tr>
                    <th scope="col">Campanha</th><th scope="col">Cliente · obra</th>
                    <th scope="col" className="n">Furos</th><th scope="col" className="n">Metragem</th>
                    <th scope="col">Criada em</th><th scope="col">Situação</th>
                  </tr>
                </thead>
                <tbody>
                  {linhas.map((c) => (
                    <tr key={c.id}>
                      <th scope="row">
                        <Link to={`/spt/${c.id}`} className="mono">{c.numero}</Link>
                        <span className="sub">{c.titulo}</span>
                      </th>
                      <td>{c.cliente ?? '—'}{c.obra && <span className="sub">{c.obra}</span>}</td>
                      <td className="n mono">{c.furos}</td>
                      <td className="n mono">{c.furos ? `${metro(c.metragem)} m` : '—'}</td>
                      <td>{dataDoDia(c.criada_em)}</td>
                      <td><SeloDaCampanha situacao={c.situacao} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p className="sub">{plural(linhas.length, 'campanha', 'campanhas')}</p>
            </div>
          )}
      </div>
      {nova && <JanelaDeNovaCampanhaSpt aoFechar={() => setNova(false)} />}
    </div>
  );
}

export function JanelaDeNovaCampanhaSpt({ os, aoFechar }: { os?: { id: string; numero: string }; aoFechar(): void }) {
  const navegar = useNavigate();
  const [osId, setOsId] = useState<string | null>(os?.id ?? null);
  const [titulo, setTitulo] = useState('');
  const [erro, setErro] = useState<unknown>(null);
  const [ocupado, setOcupado] = useState(false);
  const falta = !osId ? 'Escolha a ordem de serviço da campanha.' : !titulo.trim() ? 'Dê um título à campanha.' : null;
  const criar = async () => {
    if (!osId || falta || ocupado) return;
    setOcupado(true); setErro(null);
    try { const r = await criarCampanhaSpt(osId, titulo.trim()); navegar(`/spt/${r.id}`); }
    catch (e) { setErro(e); setOcupado(false); }
  };
  return (
    <Dialogo aberto titulo="Nova campanha SPT" rotulo={os ? `OS ${os.numero}` : undefined} aoFechar={aoFechar}>
      <form onSubmit={(e) => { e.preventDefault(); void criar(); }}>
        <ErroDaApi erro={erro} />
        {!os && <SeletorDeOs valor={osId} cliente={null} aoMudar={setOsId} />}
        <Campo rotulo="Título da campanha" value={titulo} maxLength={200} required
          dica="Como a campanha aparece na lista e no relatório — ex.: «Galpão — 6 furos SPT»."
          onChange={(e) => setTitulo(e.target.value)} />
        <p className="sub">A campanha nasce em rascunho, com o número da OS (ex.: 2608328-SPT-01). Cliente e obra vêm da OS.</p>
        {falta && <p className="spt-falta" role="status">{falta}</p>}
        <div className="rodape-dialogo">
          <button type="submit" className="botao" aria-disabled={!!falta || ocupado}>{ocupado ? 'Criando…' : 'Criar a campanha'}</button>
          <button type="button" className="botao secundario" onClick={aoFechar}>Cancelar</button>
        </div>
      </form>
    </Dialogo>
  );
}
